import React, { Component } from 'react';
import PropTypes from 'prop-types';
import domtoimage from 'dom-to-image';
import getBrowser from '../../utils/getBrowser';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import { min, max, zip } from 'd3-array';
import { line } from 'd3-shape';
import { scaleLinear } from 'd3-scale';
import SimpleAxis from './SimpleAxis';
import PriceIndicator from './PriceIndicator';
import { withStyles } from '@material-ui/core/styles';
import Fab from '@material-ui/core/Fab';
import GetAppIcon from '@material-ui/icons/GetApp';

const description = [
  [
    'Basis:',
    ' Verbraucherpreise für die Abnahme von 33.540 kWh Gas (Ho), 27.820 kWh Fernwärme inkl. Grundpreis',
  ],
  [
    'und MwSt., 4.603 l Flüssiggas inkl. Tankmiete und MwSt., 3.000 l Heizöl EL (Hu: 10 kWh/l) bzw. 6 t Pellets ENplus A1',
  ],
  [
    '(Hu: 5 kWh/kg, inkl. MwSt. und sonstige Kosten). ',
    'Quellen:',
    ' Solar Promotion GmbH (Pelletpreis bis 2010), Deutsches',
  ],
  [
    'Pelletinstitut GmbH (Pelletpreis ab 2011), Brennstoffspiegel (Heizöl-, Flüssiggas-, Fernwärme und Erdgaspreise),',
  ],
  ['Verbraucherzentrale NRW (Wärmepumpenstrom und Jahresarbeitszahl (JAZ))'],
  ['© Deutsches Pelletinstitut GmbH'],
];

const styles = theme => ({
  wrapper: {
    width: '100%',
    position: 'relative',
    '&:hover $downloadButton': {
      visibility: 'visible',
    },
  },
  downloadButton: {
    visibility: 'hidden',
    backgroundColor: theme.editorPalette.primary.main,
    color: theme.editorPalette.common.white,
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    '&:hover': {
      backgroundColor: theme.editorPalette.primary.dark,
    },
  },
  chart: {
    width: '100%',
    height: 'auto',
    '@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none)': {
      height: '500px !important',
      width: 'auto !important',
    },
    '@global': {
      'line, rect': {
        shapeRendering: 'optimizeSpeed',
      },
      path: {
        shapeRendering: 'optimizeQuality',
      },
    },
  },
  footnote: {
    ...theme.typography.body2,
    lineHeight: 1.6,
    fill: theme.editorPalette.grey[600],
  },
  unit: {
    ...theme.typography.caption,
    fill: theme.editorPalette.secondary.main,
  },
  title: {
    ...theme.typography.h6,
    textAlign: 'center',
    fill: theme.editorPalette.secondary.main,
  },
  backgroundTicks: {
    '@global': {
      line: {
        stroke: theme.editorPalette.grey[100],
      },
    },
  },
  chartFrame: {
    stroke: theme.editorPalette.grey[100],
    fill: 'none',
  },
  chartLines: {
    '@global': {
      path: {
        strokeLinecap: 'round',
      },
    },
  },
  type: {
    fontWeight: 'bold',
  },
  ticks: {
    '@global': {
      line: {
        stroke: theme.editorPalette.secondary.main,
      },
      text: {
        ...theme.typography.caption,
        fill: theme.editorPalette.secondary.main,
      },
    },
  },
});

@observer
class PriceChartRenderer extends Component {
  static propTypes = {
    data: PropTypes.array,
    chartMargin: PropTypes.object,
  };

  static defaultProps = {
    data: [[]],
    chartMargin: {
      top: 40,
      right: 130,
      bottom: 150,
      left: 48,
    },
  };

  componentDidMount() {
    this.browser = getBrowser();
  }

  @action.bound
  setRef(ref) {
    this.domNode = ref;
  }

  @action.bound
  setContainerRef(ref) {
    this.containerDomNode = ref;
  }

  browser = null;

  downloadAs = type => {
    if (!type || !this.containerDomNode) return false;
    // const filterFn = node => (node && node.getAttribute && !node.getAttribute('class')) || (node && node.getAttribute && (!node.getAttribute('class').includes(cx('download')) && !node.getAttribute('class').includes(cx('button'))));
    const options = { bgcolor: '#fff' };
    if (type === 'png') {
      domtoimage.toBlob(this.containerDomNode, options).then(dataUrl => {
        const link = document.createElement('a');
        link.download = 'DEPI-Grafik-Brennstoffkosten.png';
        link.href = URL.createObjectURL(
          new Blob([dataUrl], {
            type: 'image/png',
          }),
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
      return true;
    }
    return false;
  };

  @observable domNode = null;
  @observable containerDomNode = null;
  @observable svgWidth = 965;
  @observable svgHeight = 500;

  render() {
    const { data, chartMargin, classes } = this.props;
    if (data.length === 0) return null;
    // these sizes are needed at a few places, so we precalculate them
    const chartWidth = this.svgWidth - (chartMargin.left + chartMargin.right);
    const chartHeight = this.svgHeight - (chartMargin.top + chartMargin.bottom);

    // we use this specification to generate the lines, styles and other stuff
    // this specifation is kind of hard wired to the table format
    const columns = [1, 2, 3, 4, 5, 6]; // the columns to take from the data array
    const labels = [
      'Pellets',
      'Heizöl',
      'Erdgas',
      'Flüssiggas',
      'Fernwärme',
      'Wärmepumpe',
    ];
    const colors = [
      '#E15D00',
      '#cccccc',
      '#989898',
      '#686868',
      '#3a3a3a',
      '#121212',
    ];

    // convert all columns to numbers [exception: dont convert first column (months) or empty values (might be on purpose)]
    const convertCell = ({ value }, i) =>
      i > 0 && value !== '' ? +value.replace(',', '.') : value;

    // apply this on the data, but remove the first row (which are labels)
    const convertedData = data
      .filter((f, i) => i > 0)
      .map(row => row.map(convertCell));

    // will be needed quite often, so we extract the month from the data
    const monthData = convertedData.map(d => d[0]);

    // these are required for the scales
    const minPrice = min(convertedData, d =>
      min(columns.map(col => d[col]).filter(f => f !== '')),
    );
    const maxPrice = max(convertedData, d =>
      max(columns.map(col => d[col]).filter(f => f !== '')),
    );

    // scales
    const xScale = scaleLinear()
      .domain([0, monthData.length - 1])
      .range([chartMargin.left, chartMargin.left + chartWidth]);
    const yScale = scaleLinear()
      .domain([maxPrice, minPrice])
      .range([chartMargin.top, this.svgHeight - chartMargin.bottom])
      .nice();

    // map to scale && path generator
    const lineGenerator = line()
      .x((d, i) => xScale(i))
      .y(d => yScale(d.value))
      .defined(d => d.value && d.value !== '');

    const lineSpecs = zip(columns, labels, colors);

    // these values will be used later to generate the indicators/labels
    const lineValues = lineSpecs.map(([column, label, color]) => ({
      data: convertedData.map(d => ({ value: d[column], month: d[0] })), // data = for every row, get value of [column] and the month
      color,
      label,
    }));

    const indicatorValues = lineValues
      .map(m => {
        // we have to accomodate for empty values
        // only values that are not ''
        const notEmptyValues = m.data
          .map((val, i) => ({ ...val, index: i }))
          .filter(f => f.value !== '');

        // return null if there are no values --> no indicator
        const hasAnyValues = m.data.filter(f => f.value !== '').length > 0;
        if (!hasAnyValues) return null;
        // the last value that is valid, we use it to calculate the position of the indicator
        const lastValueThatIsNotEmpty =
          notEmptyValues[notEmptyValues.length - 1];
        // position of the last data point
        const lastDatapointPosition = {
          x: xScale(lastValueThatIsNotEmpty.index) + 5,
          y: yScale(lastValueThatIsNotEmpty.value),
        };
        // the position of the label
        const indicatorPosition = {
          x: xScale(m.data.length - 1) + 16,
          y: yScale(lastValueThatIsNotEmpty.value),
        };
        // the props we put into the indicator
        const backgroundColor = m.label === 'Pellets' ? m.color : null;
        const textColor = m.label === 'Pellets' ? '#fff' : '#34322E';
        const label =
          m.label === 'Pellets'
            ? `${lastValueThatIsNotEmpty.value} ct/kWh`
            : m.label;
        return {
          lastDatapointPosition,
          indicatorPosition,
          backgroundColor,
          textColor,
          label,
          type: m.label === 'Pellets' ? 'pellet' : 'other',
        };
      })
      .filter(f => !!f);

    // constraint relaxation on labels (prevents overlapping)
    const alpha = 0.5;
    let again = true;
    while (again) {
      again = false;
      indicatorValues.forEach((labelA, i) => { // eslint-disable-line
        indicatorValues.slice(i + 1).forEach(labelB => {
          const deltaY =
            labelA.indicatorPosition.y - labelB.indicatorPosition.y;
          const fixedA = labelA.type === 'pellet';
          const fixedB = labelB.type === 'pellet';
          const spacing = fixedA || fixedB ? 40 : 15;
          if (Math.abs(deltaY) < spacing) {
            // to allow another iteration at least one must be allowed to move
            again = true;
            const sign = deltaY > 0 ? 1 : -1;
            labelA.indicatorPosition.y += fixedA ? 0 : sign * alpha;
            labelB.indicatorPosition.y -= fixedB ? 0 : sign * alpha;
          }
        });
      });
    }

    // the line path component
    const lines = lineValues.map(({ data: lineData, color, label }) => (
      <path
        key={label}
        style={{
          stroke: color,
          strokeWidth: label === 'Pellets' ? 5 : 2,
          fill: 'none',
        }}
        d={lineGenerator(lineData)}
      />
    ));

    // grey ticks in bg and sides
    // eslint-disable-next-line
    const yTickLines = yScale.ticks().map((m, i) => <line key={`x-tick-${i}`} x1={chartMargin.left} x2={chartMargin.left + chartWidth} y1={yScale(m)} y2={yScale(m)} />);
    // eslint-disable-next-line
    const xTickLines = monthData.map((m, i) => <line key={`y-tick-${i}`} x1={xScale(i)} x2={xScale(i)} y1={chartMargin.top} y2={chartMargin.top + chartHeight} />);

    // props for the simple axis component (x)
    const xAxisProps = {
      className: classes.ticks,
      ticks: Array(monthData.length)
        .fill(undefined)
        .map((m, i) => i), // we have to hardcode the ticks
      style: {
        transform: `translate(0, ${this.svgHeight - chartMargin.bottom}px)`,
      },
      axis: 'x',
      scaleFunc: xScale,
      labelPadding: 8,
      tickSize: 8,
      labelFormat: (d, i) => monthData[i], // replace the tickvalue with a value of the tick-labels (allows 'empty' ticks)
    };

    // props for the simplaxis component (y)
    const yAxisProps = {
      skipExtent: true,
      className: classes.ticks,
      style: { transform: `translate(${chartMargin.left}px, 0)` },
      axis: 'y',
      scaleFunc: yScale,
      labelPadding: 8,
      tickSize: 8,
    };

    return (
      <div className={classes.wrapper}>
        <div ref={ref => this.setContainerRef(ref)}>
          <svg
            className={classes.chart}
            height={this.svgHeight}
            width={this.svgWidth}
            viewBox={`0 0 ${this.svgWidth} ${this.svgHeight}`}
            preserveAspectRatio="xMaxYMax"
            ref={ref => this.setRef(ref)}
          >
            <text
              x={chartMargin.left}
              y={chartMargin.top - 10}
              className={classes.unit}
            >
              Preis in ct/kWh
            </text>
            <text
              textAnchor="middle"
              x={this.svgWidth / 2}
              y="20"
              className={classes.title}
            >
              Brennstoffkosten in Deutschland
            </text>
            <g className={classes.backgroundTicks} key="background-ticks">
              {xTickLines}
              {yTickLines}
            </g>
            <SimpleAxis {...xAxisProps} />
            <SimpleAxis {...yAxisProps} />
            <g className={classes.chartLines} key="chart-lines">
              {lines.reverse()}
            </g>
            <g key="indicators">
              {indicatorValues.map(
                ({
                  indicatorPosition,
                  lastDatapointPosition,
                  backgroundColor,
                  textColor,
                  label,
                  type,
                }) => {
                  // always return the current label (add it to the list)
                  const priceLabel = (
                    <PriceIndicator
                      x={indicatorPosition.x}
                      y={indicatorPosition.y}
                      key={`label-${label}`}
                      label={label}
                      backgroundColor={backgroundColor}
                      textColor={textColor}
                    />
                  );
                  // Add an additional label if the label is for the pellet price.
                  // We also have to make sure that the additional label is not outside of the chart...
                  let additionalPriceLabel = null;
                  if (type === 'pellet') {
                    const offset = 22;
                    let newYPosition = indicatorPosition.y - offset;
                    if (newYPosition < offset)
                      newYPosition = indicatorPosition.y + offset;
                    if (newYPosition > this.svgHeight - offset)
                      newYPosition = indicatorPosition.y - offset;
                    additionalPriceLabel = (
                      <PriceIndicator
                        x={indicatorPosition.x}
                        y={newYPosition}
                        key={`additional-label-${label}`}
                        label="Pellets"
                        backgroundColor={null}
                        textColor={backgroundColor}
                      />
                    );
                  }
                  // create the line that connects the label with the last data point
                  const indicatorLine = (
                    <line
                      style={{
                        stroke: type === 'pellet' ? backgroundColor : textColor,
                        opacity: 0.5,
                        strokeWidth: 1,
                        strokeDasharray: '3, 3',
                        shapeRendering: 'geometricPrecision',
                      }}
                      key={`line-${label}`}
                      x1={indicatorPosition.x - 5}
                      y1={indicatorPosition.y}
                      x2={lastDatapointPosition.x}
                      y2={lastDatapointPosition.y}
                    />
                  );
                  return (
                    <g key={`indicator-${label}`}>
                      {indicatorLine}
                      {priceLabel}
                      {additionalPriceLabel}
                    </g>
                  );
                },
              )}
            </g>
            <g key="descriptions">
              {description
                // this stupid hack generates a fake multi line text paragraph in svg text
                .map((textrow, i) => (
                  <text
                    key={textrow.join('')}
                    className={classes.footnote}
                    x={chartMargin.left}
                    y={this.svgHeight - chartMargin.bottom + 60 + i * 22}
                  >
                    {textrow.map(text => (
                      <tspan
                        key={text}
                        className={
                          text.endsWith(':') ? classes.type : classes.text
                        }
                      >
                        {text}
                      </tspan>
                    ))}
                  </text>
                ))}
            </g>
          </svg>
        </div>
        {this.browser &&
          this.browser !== 'ie' &&
          this.browser !== 'edge' &&
          this.browser !== 'safari' && (
              <Fab
                onClick={() => this.downloadAs('png')}
                className={classes.downloadButton}
              >
                <GetAppIcon />
              </Fab>
          )}
      </div>
    );
  }
}

export default withStyles(styles)(PriceChartRenderer);
